import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Table from "../../core/common/dataTable/index";
import { ColumnsType } from "antd/es/table";
import { all_routes } from "../router/all_routes";
import useApi from "../../core/customhook/useApi";
import BreadCrumbForm from "../all-forms/common-components/BreadCrumbForm";
import Loading from "../../core/common/custom-loading/Loading";
import { get_enabled_monthly_list } from "../../environment";
import { formattedDate } from "../../core/common/formattedDate";
import dayjs from "dayjs";

export interface DataProps {
  emp_id: number;
  emp_no: string;
  full_name: string;
  department: string;
  email_id: string;
  assessment_id: number;
  enabled_on: string;
  submitted_on: string;
  final_decision: string;
  is_enable: number;
  is_form_filled: number;
  is_form_monthly_filled: number;
}

interface PaginationProps {
  current: number;
  pageSize: number;
  total: number;
}

// ('Pending', 'Selected', 'Rejected', 'Extension')
const statusStyle: Record<string, string> = {
  pending: "",
  selected: "badge-success badge-xs text-white",
  rejected: "badge-danger badge-xs text-white",
  extension: "badge-warning badge-xs text-white",
};

const EnabledMonthlyList = () => {
  const navigate = useNavigate();
  const [allItemList, setAllItemList] = useState<DataProps[]>([]);
  const [pagination, setPagination] = useState<PaginationProps>({
    current: 1,
    pageSize: 10, // limit of records
    total: 1,
  });

  //   -- api
  const { callApi, loading, error } = useApi("", {
    method: "GET",
    autoCall: false,
  });


  const columns: ColumnsType<any> = [
    {
      title: "Sr No.",
      dataIndex: "SrNo",
      render: (text: string, record: any, index: any) => (
        <h6
          className="fw-medium fs-14 text-dark text-center"
          style={{ width: 50 }}
        >
          {index + 1}
        </h6>
      ),
      sorter: false,
    },
    {
      title: "Month",
      render: (text: string, record: any) => <>{dayjs(record?.enabled_on)?.format("MMMM")}</>
    },
    {
      title: "Enabled on",
      dataIndex: "enabled_on",
      render: (text: string, record: any) => {
        const date = record?.is_enable
          ? formattedDate(text)
          : "-";
        return <div style={{ width: 145 }}>{date}</div>;
      },
      sorter: (a: any, b: any) => {
        const dateA = a.enabled_on
          ? new Date(a.enabled_on).getTime()
          : 0;
        const dateB = b.enabled_on
          ? new Date(b.enabled_on).getTime()
          : 0;
        return dateA - dateB;
      },
    },
    {
      title: "Submitted on",
      dataIndex: "filled_on",
      render: (text: string, record: any) => {
        const date = text
          ? formattedDate(text)
          : "-";
        return <div style={{ width: 145 }}>{date}</div>;
      },
      sorter: (a: any, b: any) => {
        const dateA = a.filled_on
          ? new Date(a.filled_on).getTime()
          : 0;
        const dateB = b.filled_on
          ? new Date(b.filled_on).getTime()
          : 0;
        return dateA - dateB;
      },
    },
    {
      title: "Form Status",
      dataIndex: "status",
      render: (text: string, record: any) => <span className="text-capitalize">{text}</span>,
      sorter: (a: any, b: any) =>
        (a.status ?? "").localeCompare(b.status ?? ""),
    },
    {
      title: "Action",
      render: (text: string, record: any) => {
        return (
          <div style={{ minWidth: "80px" }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm d-flex align-items-center py-1 mx-auto"
              // disabled={record?.filled_on}
              onClick={() =>
                navigate(
                  `${all_routes.monthlyFeedbackFormByID}/${record?.id}/${record?.employee_id}`, // id : form_id
                )
              }
            >
              {!!record?.filled_on ? <><i className="ti ti-eye pe-1"></i>
                View</> :
                <><i className="ti ti-edit pe-1"></i>
                  Fill form</>}
            </button>
          </div>
        );
      },
    },
  ];

  const fetchData = useCallback(async () => {
    try {
      const { current, pageSize } = pagination;
      const resp = await callApi(
        undefined,
        `${get_enabled_monthly_list}`,
        // `${get_new_joiners}?page=${current}&limit=${pageSize}`,
      );
      // console.log("response:", resp.data);
      if (resp?.success) {
        const final = resp?.data;
        const pageData = resp?.pagination;
        setAllItemList(final?.length > 0 && final);
        setPagination({
          current: pageData?.currentPage,
          pageSize: pageData?.limit,
          total: pageData?.total,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <BreadCrumbForm
            title="Monthly Feedback Forms"
            linkTitle="Monthly Feedback Forms"
            prevMenu={false}
          />
          {/* /Breadcrumb */}
          {/* Performance Indicator list */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Monthly Feedback List</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <Loading />
              ) : (
                <Table
                  dataSource={allItemList}
                  columns={columns}
                  Selection={false}
                  totalEmployees={pagination.total}
                  currentPage={pagination.current}
                  setCurrentPage={(page) =>
                    setPagination((prev) => ({ ...prev, current: page }))
                  }
                  limit={pagination.pageSize}
                  setLimit={(limit) =>
                    setPagination((prev) => ({ ...prev, pageSize: limit }))
                  }
                />
              )}
            </div>
          </div>
          {/* /Performance Indicator list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
          <p className="mb-0">© 2025 WeConnect. All rights reserved.</p>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default EnabledMonthlyList;
